import React, { useState, useEffect } from 'react';
import { fetchApplicantByEmail, createApplicant, updateApplicant, fetchApplicantByAadharNumber } from '../../helpers/Espo/applicant_helper';
import { Button, Collapse, Card, CardBody, CardHeader, Col, Row, CardTitle } from 'reactstrap';
import classnames from 'classnames';
import DynamicForm from '../Forms/DynamicForm';
import ApplicationList from '../Application/ApplicationList';

const Applicant = () => {
    const [applicantData, setApplicantData] = useState(null); // Initialize as null
    const [loading, setLoading] = useState(true);
    const [applicantExists, setApplicantExists] = useState(false);
    const [email, setEmail] = useState("");
    const [col1, setCol1] = useState(false);

    // Fetch User Email
    useEffect(() => {
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
            const obj = JSON.parse(authUser);
            setEmail(obj.email);
        }
    }, []);

    // Load applicant data
    useEffect(() => {
        const loadApplicant = async () => {
            try {
                if (email) {
                    console.log("Loading applicant for email:", email);
                    const response = await fetchApplicantByEmail(email);

                    if (response && response.total > 0) {
                        console.log("loadApplicant Response - ", response.list[0]);
                        setApplicantData(response.list[0]);
                        setApplicantExists(true);
                        setCol1(false); // Collapse the form since applicant exists
                    } else {
                        console.log("No existing applicant found, preparing for new registration");
                        setApplicantData({ emailAddress: email });
                        setApplicantExists(false);
                        setCol1(true); // Expand the form for new registration
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error("Error loading applicant:", error);
                // Still allow the user to proceed with registration
                setApplicantData({ emailAddress: email });
                setApplicantExists(false);
                setCol1(true);
                setLoading(false);
            }
        };

        loadApplicant();
    }, [email]);

    // Pass formData to this function
    const handleSubmit = async (formData) => {
        try {
            let submissionData;
            
            if (applicantExists) {
                // For existing applicants, remove Aadhaar file-related keys from formData 
                // since file uploads are handled separately when user uploads files
                const { 
                    aadharFront, 
                    aadharFrontId, 
                    aadharFrontName, 
                    aadharBack, 
                    aadharBackId, 
                    aadharBackName, 
                    ...dataWithoutAadhar 
                } = formData;
                submissionData = dataWithoutAadhar;
            } else {
                // For new applicants, include all Aadhar-related information in the payload
                // to ensure that Aadhar details are saved even if files are uploaded before applicant creation
                submissionData = formData;
            }
            
            //check if applicant aadhar number exists
            if (submissionData.aadharNumber) {
                const response = await fetchApplicantByAadharNumber(submissionData.aadharNumber);
                // show alert If response is not empty and applicant exists, and it's not the current applicant being edited
                if (response && response?.total > 0 && response?.list?.[0]?.emailAddress !== email) {
                    alert("Applicant with this Aadhar Number already exists.");
                    return;
                }
            }
            
            // Use submissionData (without Aadhaar file keys) to update or create the applicant
            if (applicantExists) {
                const updateResponse = await updateApplicant(applicantData.id, submissionData);
                alert("Applicant updated successfully!");
                
                // Reload the applicant data to reflect changes
                if (updateResponse && updateResponse.data) {
                    setApplicantData(updateResponse.data);
                } else {
                    // Fallback: fetch the updated data
                    const refreshResponse = await fetchApplicantByEmail(email);
                    if (refreshResponse && refreshResponse.total > 0) {
                        setApplicantData(refreshResponse.list[0]);
                    }
                }
            } else {
                try {
                    const createResponse = await createApplicant(submissionData);
                    alert("New applicant created successfully!");
                    setApplicantExists(true);
                    
                    // Set the new applicant data
                    if (createResponse && createResponse.data) {
                        setApplicantData(createResponse.data);
                    } else {
                        // Fallback: fetch the created data
                        const refreshResponse = await fetchApplicantByEmail(email);
                        if (refreshResponse && refreshResponse.total > 0) {
                            setApplicantData(refreshResponse.list[0]);
                            setApplicantExists(true);
                        }
                    }
                } catch (createError) {
                    console.error("Create applicant error:", createError);
                    
                    // Handle 409 Conflict - applicant already exists
                    if (createError.response && createError.response.status === 409) {
                        console.log("Applicant already exists, fetching existing data...");
                        
                        // Try to fetch the existing applicant data
                        const refreshResponse = await fetchApplicantByEmail(email);
                        if (refreshResponse && refreshResponse.total > 0) {
                            setApplicantData(refreshResponse.list[0]);
                            setApplicantExists(true);
                            alert("Applicant profile already exists. Data has been loaded successfully!");
                        } else {
                            alert("Applicant already exists but could not retrieve data. Please refresh the page.");
                        }
                    } else {
                        // Re-throw other errors to be caught by outer catch block
                        throw createError;
                    }
                }
            }
        } catch (error) {
            console.error("Error in handleSubmit:", error);
            
            // Provide more specific error messages
            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        alert("This applicant already exists in the system. Please check your details or contact support.");
                        break;
                    case 400:
                        alert("Invalid data provided. Please check all fields and try again.");
                        break;
                    case 422:
                        alert("Validation error. Please ensure all required fields are properly filled.");
                        break;
                    default:
                        alert(`Failed to save applicant. Server error: ${error.response.status}`);
                }
            } else if (error.request) {
                alert("Network error. Please check your internet connection and try again.");
            } else {
                alert("Failed to save applicant. Please try again.");
            }
        }
    };

    if (loading) return <div>Loading...</div>; // Show loading state until data is loaded

    const toggleCol1 = () => {
        setCol1(!col1);
    };

    const aidApplicantSchema = require('../Forms/Schema/AidApplicant.json');

    return (
        <React.Fragment>
            <Row>
                <Col lg={12}>
                    <Card className="accordion" id="accordion">
                        <CardTitle hidden={applicantExists} className="alert alert-warning">
                            Thank you for registering. Please complete your personal profile first so that you can proceed with the application.
                        </CardTitle>

                        <div className="accordion-item">
                            <CardHeader className="h4 border-bottom accordion-header" id="headingOne">
                                <button
                                    className={classnames(
                                        "accordion-button",
                                        "fw-medium",
                                        { collapsed: !col1 }
                                    )}
                                    type="button"
                                    onClick={toggleCol1}
                                    style={{ cursor: "pointer" }}
                                >
                                    Personal Profile
                                </button>
                            </CardHeader>

                            <Collapse isOpen={col1} className="accordion-collapse">
                                <div className="accordion-body">
                                    <CardBody>
                                        <DynamicForm
                                            schema={aidApplicantSchema}
                                            initialValues={applicantData || {}} // Ensure applicantData is ready
                                            onSubmit={(formData) => handleSubmit(formData)}
                                            stepperOn={false}
                                        />
                                    </CardBody>
                                </div>
                            </Collapse>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Application List */}
            {!loading && applicantData && applicantExists && (
                <Row>
                    <Col lg={12}>
                        <ApplicationList applicant={applicantData} />
                    </Col>
                </Row>
            )}
        </React.Fragment>
    );
};

export default Applicant;
